import { useQuery } from '@apollo/client';
import {
  getCompaniesEmailRecordsQuery,
  getReviewEmailRecordsQuery,
} from '../../../queries/emailRecord';
import React, { useEffect, useState } from 'react';
import { BookletCompanyReview, Coupon, EmailRecord } from '../../../types/generated.types';
import convertStatus from '../utils/convertStatus';
import { getCompanyBookletReviewsQuery } from '../../../queries/bookletCompanyReview';
import { BookletTitle } from '../BookletTitle';
import EmailSection from './EmailSection';

interface props {
  bookletId?: any;
  zoneId: any;
  companyId?: any;
  isReview: boolean;
  selectedCompanies?: any[]; // selected companies on companies page
}

export interface CombinedRecord {
  id: string;
  isReview: boolean;
  status: string;
  message: string;
  date: Date;
  companyName: string;
  bookletTitle: string;
  coupons: Coupon[];
  data: EmailRecord | BookletCompanyReview;
}

function HistoryTab({
  bookletId,
  zoneId,
  companyId,
  isReview,
  selectedCompanies,
}: props) {
  const [selectedRecord, setSelectedRecord] = useState<CombinedRecord | null>(
    null
  );

  const companyIds = selectedCompanies?.map(item => item.company._id) || [];
  const bookletIds = selectedCompanies?.map(item => item.booklet._id) || [];

  const {
    data: reviewEventData,
    loading: reviewsLoading,
    refetch: refetchReviewEvents,
  } = useQuery(getCompanyBookletReviewsQuery, {
    variables: {
      companyId: companyId,
      bookletId: bookletId
    },
    fetchPolicy: 'network-only',
    skip: !isReview
  })

  const {
    data: emailRecordData,
    loading: emailsLoading,
    refetch: refetchReviewEmails,
  } = useQuery(
    isReview ? getReviewEmailRecordsQuery : getCompaniesEmailRecordsQuery,
    {
      variables: {
        zoneId: zoneId,
        ...(!isReview && { companyIds: companyIds }),
        ...(isReview && { companyId: companyId, bookletId: bookletId }),
      },
      fetchPolicy: 'network-only',
    }
  );

  //Combine reviews and emails to a single combined array
  const allEvents: CombinedRecord[] = [];

  if (emailRecordData?.emailRecords) {
    emailRecordData.emailRecords.forEach((record: EmailRecord) => {
      allEvents.push({
        id: record._id,
        isReview: false,
        status: record.status,
        message: record.message,
        date: new Date(record.sent),
        companyName: record.company.name,
        bookletTitle: record.booklet.title,
        coupons: record.review?.coupons || [],
        data: record,
      });
    });
  }

  if (reviewEventData?.bookletCompanyReviews) {
    reviewEventData?.bookletCompanyReviews.forEach((review: BookletCompanyReview) => {
      allEvents.push({
        id: review._id,
        isReview: true,
        status:
          review.approvedByAdmin ? 'ADMIN_APPROVED' :
            review.approvedDate ? 'APPROVED' :
              review.comment ? 'CHANGE_REQUESTED' :
                review.isActive ? 'PENDING' : 'CANCELED',
        message: review.comment || '',
        date: new Date(review.approvedDate ? review.approvedDate : review.sentDate),
        companyName: review.company.name,
        bookletTitle: review.bookletId,
        coupons: review.coupons,
        data: review,
      });
    });
  }

  allEvents.sort((a, b) => b.date.getTime() - a.date.getTime());

  // all records if review (already filtered), only the selected booklets email otherwise
  const currentRecords: CombinedRecord[] = !isReview ? [] : allEvents || [];
  // emails for the selected companies that are not in the selected booklets
  const otherRecords: CombinedRecord[] = [];

  if (!isReview) {
    allEvents.forEach((record: CombinedRecord) => {
      if (bookletIds.includes(record.data.bookletId)) {
        currentRecords.push(record);
      } else {
        otherRecords.push(record);
      }
    });
  }

  useEffect(() => {
    const interval = setInterval(() => {
      refetchReviewEmails();
      if (isReview) {
        refetchReviewEvents();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [refetchReviewEmails, refetchReviewEvents]);

  const getFrom = (selectedRecord: CombinedRecord) => {
    if (!selectedRecord.isReview) {
      const record = selectedRecord.data as EmailRecord;

      const name = !record.user ? 'Borttagen användare' : `${record.user.firstname} ${record.user.lastname}`;
      return (
        <>
          {name} ({record.from ?? 'Ej skickat'})
        </>
      )
    }
    return selectedRecord.companyName;
  }

  return (
    <>
      <div className="section">
        <ul className="records">
          <EmailSection
            title={isReview ? 'Skickade granskningar' : 'Skickade meddelanden'}
            isLoading={emailsLoading || reviewsLoading}
            selectedRecord={selectedRecord}
            setSelectedRecord={setSelectedRecord}
            records={currentRecords}
          />
          {otherRecords.length > 0 && (
            <EmailSection
              title='Övriga häften'
              isLoading={emailsLoading || reviewsLoading}
              selectedRecord={selectedRecord}
              setSelectedRecord={setSelectedRecord}
              records={otherRecords}
            />
          )}
        </ul>
      </div >
      <div className="section">
        <h4>Information</h4>

        {selectedRecord ? (
          <div className="record-info">
            { /* Only display/receiver sender info when its an email or an event triggered by the company */}
            {(!selectedRecord.isReview || selectedRecord.status === 'CHANGE_REQUESTED' || selectedRecord.status === 'APPROVED') && (
              <>
                {(selectedRecord.status !== 'CHANGE_REQUESTED' && selectedRecord.status !== 'APPROVED') &&
                  <div>
                    <h5>Till:</h5>
                    <span> {getTo(selectedRecord)}</span>
                  </div>
                }
                <div>
                  <h5>Från:</h5>
                  <span>
                    {' '}
                    {getFrom(selectedRecord)}
                  </span>
                </div>
              </>
            )
            }
            <div>
              <h5>Skickat:</h5>
              <span> {new Date(selectedRecord.date).toLocaleString()}</span>
            </div>
            <div>
              <h5>Status:</h5>
              <span> {convertStatus(selectedRecord.status)}</span>
            </div>
            <div>
              <h5>Företag:</h5>
              <span> {selectedRecord.companyName}</span>
            </div>
            {selectedRecord.message &&
              <div>
                <h5>
                  {selectedRecord.isReview && selectedRecord.status !== 'ADMIN_APPROVED'
                    ? 'Granskningsmeddelande:'
                    : 'Meddelande:'}
                </h5>
                <span>
                  {' '}
                  {selectedRecord.message.replace(/<br\s*\/?>/gi, ' ')}
                </span>
              </div>
            }
            <div>
              <h5>Häfte:</h5>
              <span> {selectedRecord.isReview ?
                <BookletTitle bookletId={selectedRecord.bookletTitle} /> :
                selectedRecord.bookletTitle}
              </span>
            </div>
            {isReview && !selectedRecord.isReview && (
              <div>
                <h5>Kuponger:</h5>
                <ul>
                  {selectedRecord.coupons.map((coupon, index) => {
                    return (
                      <li key={index}>
                        <p
                          style={{
                            marginBottom: '10px',
                            borderBottom:
                              '1px solid rgba(128, 128, 128, 0.319)',
                          }}
                        >
                          {coupon.discount} {coupon.smallTitle} <br />
                          {coupon.terms} <br />
                          {coupon.description}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <h5>
            {isReview
              ? 'Klicka på en granskning i listan för att se mer information'
              : 'Klicka på ett meddelande i listan för att se mer information'}
          </h5>
        )}
      </div>
    </>
  );
}

export const getTo = (selectedRecord: CombinedRecord) => {
  if (!selectedRecord.isReview) {
    return (selectedRecord.data as EmailRecord).to;
  }
  return selectedRecord.companyName;
}

export default HistoryTab;
